import { CalendarDotsIcon, MapPinIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { PILL_BASE, TAG_PILL } from '@/components/cms/ui/pill-styles';
import { Card, CardHeading } from '@/components/ui/card';
import { ImageButton } from '@/components/ui/image-button';
import { formatAddressForStartingPoint } from '@/utils/locationUtils';
import { getSquiggleCropStyle } from '@/utils/squiggleCrop';
import { cn } from '@/utils/utils';
import { shownUrl } from '@/utils/localCopies';

interface ProjectOverviewProps {
  project: {
    title: string;
    heroImage: string;
    tags: string[];
    duration: number;
    views: number;
    completionRate: number;
    locationInfo: { address?: string | null; city?: string | null; starting_point?: string | null };
    createdAt: Date;
    updatedAt: Date;
  };
  storyCount: number;
  /** Picks the squiggle's crop when the project has no cover of its own. */
  coverSeed: string;
  onChangeCover: () => void;
}

/** A project page's overview: the cover with the project's tags and numbers, beside where it is and when it was made. */
export function ProjectOverview({ project, storyCount, coverSeed, onChangeCover }: ProjectOverviewProps) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-6 md:mb-8 md:gap-8 lg:grid-cols-3">
      {/* Project Hero */}
      <div className="lg:col-span-2">
        <Card className="overflow-hidden">
          <div className="relative">
            <ImageButton label="Change cover image" className="h-72 w-full rounded-lureo-radius-lg border-0" onClick={onChangeCover}>
              {project.heroImage === projectPlaceholder ? (
                // Use squiggle image with different crop areas
                <span
                  className="block w-full h-full"
                  style={getSquiggleCropStyle(coverSeed, { scaleRange: [1, 1.5], size: 'half' })}
                />
              ) : (
                <img
                  src={shownUrl(project.heroImage)}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              )}
              {/* The upload mark, always in the corner */}
              <span aria-hidden className="absolute bottom-4 right-4 transition-transform duration-lureo-duration-moderate group-hover:scale-125">
                <UploadSimpleIcon className="h-6 w-6" />
              </span>
            </ImageButton>
            {/* Only show black gradient overlay if there's a real image */}
            {project.heroImage !== projectPlaceholder && (
              <div className="absolute inset-0 opacity-lureo-photo-shade bg-linear-to-t from-lureo-photo-shade-tint/60 via-transparent to-transparent" />
            )}
            <div className="absolute bottom-6 left-6 right-6">
              <p className={`text-lureo-18 leading-relaxed ${project.heroImage === projectPlaceholder ? '' : 'text-lureo-on-emphasis'}`}>
                {/* {project.description} */}
              </p>
            </div>
          </div>

          {/* The numbers sit close under the cover, so the cover takes the room */}
          <div className="px-6 pt-6">
            {project.tags.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {project.tags.map((tag) => (
                  <span key={tag} className={cn(PILL_BASE, TAG_PILL)}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div className="text-center">
                <div className="text-lureo-24 font-bold tabular-nums text-lureo-ink-strong leading-8">{project.duration}</div>
                <div className="text-lureo-14 text-lureo-ink-subtle leading-5">Minutes</div>
              </div>
              <div className="text-center">
                <div className="text-lureo-24 font-bold tabular-nums text-lureo-ink-strong leading-8">{storyCount}</div>
                <div className="text-lureo-14 text-lureo-ink-subtle leading-5">Stories</div>
              </div>
              <div className="text-center">
                <div className="text-lureo-24 font-bold tabular-nums text-lureo-ink-strong leading-8">{project.views}</div>
                <div className="text-lureo-14 text-lureo-ink-subtle leading-5">Views</div>
              </div>
              <div className="text-center">
                <div className="text-lureo-24 font-bold tabular-nums text-lureo-ink-strong leading-8">{project.completionRate}%</div>
                <div className="text-lureo-14 text-lureo-ink-subtle leading-5">Completion</div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Project Stats: the two cards and the gap between them take the hero card's height */}
      <div className="flex flex-col gap-4 md:gap-6">
        <Card className="flex-1">
          <CardHeading icon={<MapPinIcon />} title="Location" className="min-h-7" />
          <div className="space-y-3">
            <div>
              <div className="text-lureo-14 text-lureo-ink leading-5">Starting Point</div>
              <div className="text-lureo-14 font-light select-text text-lureo-ink-strong leading-5">
                {(() => {
                  const loc = project.locationInfo;
                  const sp = loc?.starting_point;
                  const display = (sp && sp !== 'null') ? sp : (loc?.address && loc.address !== 'null') ? formatAddressForStartingPoint(loc.address, loc.city) : '';
                  return display || 'Not chosen';
                })()}
              </div>
            </div>
            <div>
              <div className="text-lureo-14 text-lureo-ink leading-5">City</div>
              <div className="text-lureo-14 font-light select-text text-lureo-ink-strong leading-5">
                {(project.locationInfo.city && project.locationInfo.city !== 'null') ? project.locationInfo.city : 'Not chosen'}
              </div>
            </div>
          </div>
        </Card>

        <Card className="flex-1">
          <CardHeading icon={<CalendarDotsIcon />} title="Timeline" className="min-h-7" />
          <div className="space-y-3">
            <div>
              <div className="text-lureo-14 text-lureo-ink leading-5">Created</div>
              <div className="text-lureo-14 font-light text-lureo-ink-strong leading-5">
                {formatDistanceToNow(project.createdAt, { addSuffix: true })}
              </div>
            </div>
            <div>
              <div className="text-lureo-14 text-lureo-ink leading-5">Last Updated</div>
              <div className="text-lureo-14 font-light text-lureo-ink-strong leading-5">
                {formatDistanceToNow(project.updatedAt, { addSuffix: true })}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
